<script setup lang="ts">
  // biome-ignore-all lint/suspicious/noExplicitAny: <intentionally any>
  import { Loader } from '@tundr/ds-components';
  import { semanticTokenVars } from '@tundr/ds-tokens';
  import { computed, ref, useAttrs, useSlots, watch } from 'vue';
  import EasyDataTable, {
    DEFAULT_CHECKBOX_COLUMN_WIDTH,
    type ServerOptions,
    type SortType,
  } from '../../internal-data-table';
  import { useFilters, useOnFiltersChange } from '../../composables';
  import { useColumnVisibility } from '../../composables/use-column-visibility.ts';
  import { useDataTableInternals } from '../../composables/use-data-table-internals.ts';
  import { useLastFixedColumnShadow } from '../../composables/use-last-fixed-column-shadow.ts';
  import type { FiltersForTable, OnChangeFiltersEvent } from '../../store';
  import { FilterTypes, hasCompactFilters } from '../../utils';
  import {
    headerClassNameFunction,
    itemClassNameFunction,
    ITEMS_PER_PAGE_DEFAULT_OPTIONS,
    rowClassNameFunction,
  } from '../../utils/table-classes.utils.ts';
  import {
    centeredContainerClass,
    dataTableContainerClass,
    tableContainerClass,
    tableHeaderClass,
    tableHeaderWrapperClass,
    tableHeaderWrapperSpacingClass,
    tableTitleClass,
    tableWithHeaderClass,
  } from '../DataTable/DataTable.css.ts';
  import { TableEmptyState } from '../TableEmptyState';
  import { TableTopBar } from '../filters';
  import DataTablePageSizeSelector from './DataTablePageSizeSelector/DataTablePageSizeSelector.vue';
  import DataTablePaginator from './DataTablePaginator/DataTablePaginator.vue';
  import DataTableSelection from './DataTableSelection/DataTableSelection.vue';
  import { paginatedTableFooterClass } from './PaginatedDataTable.css.ts';
  import type { PaginatedDataTableProps } from './PaginatedDataTable.types.ts';

  const props = withDefaults(defineProps<PaginatedDataTableProps>(), {
    loading: false,
    alternating: false,
    searchFieldName: FilterTypes.TEXT_SEARCH,
    borderCell: false,
    mustSort: false,
    selectable: false,
    hideSelectionRecap: false,
    hideGeneralFilter: false,
    hideTextSearch: false,
    hideSelectAll: false,
    showIndex: false,
    fixedCheckbox: false,
    checkboxColumnWidth: DEFAULT_CHECKBOX_COLUMN_WIDTH,
    fixedIndex: false,
    indexColumnWidth: 36,
    tableMinHeight: 400,
    autoHeight: false,
    itemsPerPageOptions: () => ITEMS_PER_PAGE_DEFAULT_OPTIONS,
    hideableColumns: false,
    nonHideableColumns: () => [],
    /*
                  Left undefined on purpose: Vue would otherwise cast the absent boolean
                  to false and permanently opt the table out of the compact filter bar.
                */
    compactFilters: undefined,
    inlineFilters: false,
  });

  const emits = defineEmits<{
    paginationAndSort: [params: ServerOptions, filters: FiltersForTable];
    search: [searchText: string, serverOptions: ServerOptions];
    filter: [OnChangeFiltersEvent];
    rowClick: [row: any];
    middleclickRow: [row: any];
    rowDoubleClick: [row: any];
  }>();

  defineOptions({
    inheritAttrs: false,
  });

  const attrs = useAttrs();
  const slots = useSlots();

  const filtersStore = useFilters(props.filterNamespace);

  const columnVisibility = useColumnVisibility(
    props.columnVisibilityNamespace ?? '',
    props.nonHideableColumns,
  );

  const visibleHeaders = computed(() => {
    if (!props.hideableColumns || !props.columnVisibilityNamespace)
      return props.headers;
    return columnVisibility.getVisibleColumns(props.headers);
  });

  const tableHasLastFixedColumn = computed(() => {
    return visibleHeaders.value.some((header) => header.lastFixed);
  });

  const tableContainerRef = ref<HTMLElement | null>(null);
  const { showLastFixedColumnShadow } =
    useLastFixedColumnShadow(tableContainerRef);

  const hasFooterSlot = computed(() => {
    return slots && !!slots?.footer;
  });

  const dataTableClasses = computed(() =>
    [
      'tundr-table',
      dataTableContainerClass,
      hasFooterSlot.value && 'with-footer',
      props.selectable && 'selectable',
      props.clickableRows && 'clickable',
      props.hideSelectAll && 'hide-all-selector',
      tableHasLastFixedColumn.value && 'table-with-last-fixed-column',
      tableHasLastFixedColumn.value &&
        showLastFixedColumnShadow.value &&
        'show-last-fixed-column-shadow',
      props.showLastRowBorder && 'last-row-border',
    ]
      .filter(Boolean)
      .join(' '),
  );

  const {
    tableCustomHeight,
    itemsSlotsNames,
    onRowClick,
    rowIsClickable,
    rowsAreInteractive,
    onMiddleclickRow,
    resetTableScroll,
  } = useDataTableInternals({
    tableMinHeight: props.tableMinHeight,
    tableHeight: props.height,
    emits,
    headers: () => visibleHeaders.value,
    selectable: () => props.selectable,
    clickableRows: () => props.clickableRows === true,
  });

  const serverOptionSortBy = ref<string | string[] | undefined>();
  const serverOptionSortByType = ref<SortType | SortType[] | undefined>();

  const serverOptions = computed<ServerOptions>({
    get() {
      return {
        page: props.paginationOptions.currentPage,
        rowsPerPage: props.paginationOptions.rowsPerPage,
        sortBy: serverOptionSortBy?.value ?? props.sortOptions?.sortBy,
        sortType: serverOptionSortByType.value ?? props.sortOptions?.sortType,
      };
    },
    set(newValue) {
      const headerConfigFromSortName = props.headers.find((header) => {
        return newValue.sortBy === header.value && !!header.sortFieldName;
      });

      serverOptionSortByType.value = newValue.sortType;
      serverOptionSortBy.value = headerConfigFromSortName
        ? headerConfigFromSortName.sortFieldName
        : newValue.sortBy;

      emits(
        'paginationAndSort',
        { ...newValue, sortBy: serverOptionSortBy.value },
        { ...filtersStore.getFiltersForTable.value },
      );
    },
  });

  const maxPages = computed(() => {
    return Math.ceil(
      props.paginationOptions.count / props.paginationOptions.rowsPerPage,
    );
  });

  const onSetPageSize = (pageSize: string | number) => {
    const pageSizeNum =
      typeof pageSize === 'string' ? parseInt(pageSize, 10) : pageSize;
    resetTableScroll();
    emits(
      'paginationAndSort',
      {
        ...serverOptions.value,
        page: 1,
        rowsPerPage: pageSizeNum,
      },
      { ...filtersStore.getFiltersForTable.value },
    );
  };

  const onSetPage = (page: number) => {
    resetTableScroll();
    emits(
      'paginationAndSort',
      {
        ...serverOptions.value,
        page: page,
        rowsPerPage: props.paginationOptions.rowsPerPage,
      },
      { ...filtersStore.getFiltersForTable.value },
    );
  };

  const onGoToFirstPage = () => {
    resetTableScroll();
    emits(
      'paginationAndSort',
      {
        ...serverOptions.value,
        rowsPerPage: props.paginationOptions.rowsPerPage,
        page: 1,
      },
      { ...filtersStore.getFiltersForTable.value },
    );
  };

  const onGoToLastPage = () => {
    resetTableScroll();
    emits(
      'paginationAndSort',
      {
        ...serverOptions.value,
        rowsPerPage: props.paginationOptions.rowsPerPage,
        page: maxPages.value,
      },
      { ...filtersStore.getFiltersForTable.value },
    );
  };

  const onGoToNextPage = () => {
    resetTableScroll();
    emits(
      'paginationAndSort',
      {
        ...serverOptions.value,
        rowsPerPage: props.paginationOptions.rowsPerPage,
        page: props.paginationOptions.currentPage + 1,
      },
      { ...filtersStore.getFiltersForTable.value },
    );
  };

  const onGoToPrevPage = () => {
    resetTableScroll();
    emits(
      'paginationAndSort',
      {
        ...serverOptions.value,
        rowsPerPage: props.paginationOptions.rowsPerPage,
        page: props.paginationOptions.currentPage - 1,
      },
      { ...filtersStore.getFiltersForTable.value },
    );
  };

  useOnFiltersChange(props.filterNamespace, (filters) =>
    emits('filter', { ...filters }),
  );

  // Before the first fetch resolves the table looks exactly like an empty one,
  // so the controls would blink disabled on every mount: wait for a load to
  // actually complete before trusting the row count.
  const hasCompletedALoad = ref(false);

  watch(
    () => props.loading,
    (isLoading, wasLoading) => {
      if (wasLoading && !isLoading) hasCompletedALoad.value = true;
    },
  );

  // An empty list produced by a filter or a search is not an empty dataset:
  // there the user needs those very controls to get back to the full list.
  const isDatasetEmpty = computed(
    () =>
      hasCompletedALoad.value &&
      !props.loading &&
      props.items.length === 0 &&
      props.paginationOptions.count === 0 &&
      !filtersStore.isFilteringOrSearching.value,
  );
</script>

<template>
  <div :class="tableWithHeaderClass">
    <div
      v-if="!hideGeneralFilter"
      :class="[
        tableHeaderWrapperClass,
        tableTitle && filtersConfig?.length
          ? tableHeaderWrapperSpacingClass
          : undefined,
      ]"
    >
      <div :class="tableHeaderClass">
        <div v-if="tableTitle" :class="tableTitleClass">{{ tableTitle }}</div>
        <TableTopBar
          v-if="filterNamespace"
          :search-by-options="searchByOptions"
          :filter-namespace="filterNamespace"
          :search-field-name="searchFieldName"
          :search-field-placeholder="searchFieldPlaceholder"
          :filters-config="filtersConfig"
          :compact-filters="compactFilters"
          :inline-filters="inlineFilters"
          :hide-search="hideTextSearch"
          :hideable-columns="hideableColumns"
          :column-visibility-namespace="columnVisibilityNamespace"
          :non-hideable-columns="nonHideableColumns"
          :columns="headers"
          :export-action="exportAction"
          :empty-dataset="isDatasetEmpty"
        >
          <template #extra-filters-content>
            <slot name="extra-filters-content" />
          </template>
          <template #extra-action>
            <slot name="extra-action" />
          </template>
          <template #additional-filters>
            <slot name="additional-filters"></slot>
          </template>
        </TableTopBar>
      </div>
    </div>
    <div ref="tableContainerRef" :class="tableContainerClass">
      <EasyDataTable
        :table-class-name="dataTableClasses"
        :headers="visibleHeaders"
        :items="items"
        :alternating="alternating"
        :border-cell="borderCell"
        :show-index="showIndex"
        :fixed-checkbox="fixedCheckbox"
        :checkbox-column-width="checkboxColumnWidth"
        :fixed-index="fixedIndex"
        :index-column-width="indexColumnWidth"
        :table-min-height="tableMinHeight"
        :theme-color="semanticTokenVars.surface.background.brand.default.subtle"
        :rows-items="itemsPerPageOptions"
        :server-items-length="paginationOptions?.count"
        :max-items-selectable="maxItemsSelectable"
        :loading="loading"
        :must-sort="true"
        :search-field-placeholder="searchFieldPlaceholder"
        :body-item-class-name="itemClassNameFunction(visibleHeaders)"
        :body-row-class-name="
        rowClassNameFunction({
          rowIsClickable,
          customRowClassName: bodyRowClassName,
        })
      "
        :keyboard-activatable-rows="rowIsClickable && clickableRows"
        :disabled-reason="disabledReason"
        :header-item-class-name="headerClassNameFunction"
        :table-height="autoHeight ? null : tableCustomHeight"
        :no-hover="!rowsAreInteractive"
        hide-footer
        v-model:server-options="serverOptions"
        @click-row="onRowClick"
        @middleclick-row="onMiddleclickRow"
        v-bind="attrs"
      >
        <template
          v-for="slot in itemsSlotsNames"
          :key="slot"
          #[slot]="slotProps"
        >
          <slot :name="slot" v-bind="slotProps" />
        </template>

        <template #loading>
          <slot name="loading">
            <div :class="centeredContainerClass"><Loader /></div>
          </slot>
        </template>
        <template #empty-message>
          <div :class="centeredContainerClass">
            <TableEmptyState
              v-if="filtersStore.isFilteringOrSearching.value"
              filtered
              :filter-namespace="filterNamespace"
              :persistent-filters="hasCompactFilters(filtersConfig, compactFilters)"
            />
            <slot v-else name="empty-message">
              <TableEmptyState :entity-name="entityName" />
            </slot>
          </div>
        </template>
      </EasyDataTable>
    </div>
    <div :class="paginatedTableFooterClass">
      <slot name="rowSize">
        <DataTablePageSizeSelector
          :rows-per-page="paginationOptions.rowsPerPage"
          :items-per-page-options="itemsPerPageOptions"
          @setPageSize="onSetPageSize"
          :loading="loading"
        />
      </slot>
      <slot name="selection">
        <DataTableSelection
          v-if="!loading && selectable && !hideSelectionRecap"
          data-test-id="items-selected"
          :entity-name="entityName"
          :items-selected="(attrs['items-selected'] as [])?.length"
          :max-items-selectable="maxItemsSelectable"
        >
          <template #text> <slot name="selection-text"></slot> </template>
          <template #actions> <slot name="selection-actions"></slot> </template>
        </DataTableSelection>
      </slot>
      <slot name="paginator">
        <DataTablePaginator
          :total-items="paginationOptions.count"
          :items-per-page="paginationOptions.rowsPerPage"
          :loading="loading"
          :current-page="paginationOptions.currentPage"
          :is-last-page="paginationOptions.currentPage === maxPages"
          :is-first-page="paginationOptions.currentPage === 1"
          @go-to-first-page="onGoToFirstPage"
          @go-to-last-page="onGoToLastPage"
          @next-page="onGoToNextPage"
          @prev-page="onGoToPrevPage"
          @go-to-Page="onSetPage"
        />
      </slot>
    </div>
  </div>
</template>

<style>
  @import "../DataTable/DataTable-override.css";
</style>
